import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { Spinner, Modal } from "react-bootstrap";
import { BsArrowLeft } from "react-icons/bs";
import { DateTime } from "luxon";
import ApiService from "../../ApiService";
import { SnackbarContext } from "../AlertSystem";
import "./PrepAnalytics.css";

const PAGE_SIZE = 20;

const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "UTC", label: "UTC" },
];

const DEFAULT_TIMEZONE = TIMEZONE_OPTIONS[0].value;

function getInitialTimezone() {
  const browser = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return TIMEZONE_OPTIONS.some((o) => o.value === browser) ? browser : DEFAULT_TIMEZONE;
}

function formatDate(iso, tz) {
  if (!iso) return "\u2014";
  const dt = tz ? DateTime.fromISO(iso).setZone(tz) : DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat("LLL dd, yyyy") : iso;
}

function formatDateTime(iso, tz) {
  if (!iso) return "\u2014";
  const dt = tz ? DateTime.fromISO(iso).setZone(tz) : DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat("LLL dd, yyyy h:mm a ZZZZ") : iso;
}

function statusLabel(status) {
  if (!status) return "\u2014";
  switch (status.toUpperCase()) {
    case "SUBMITTED":
      return "Submitted";
    case "LEAD_CREATED":
      return "Lead Created";
    case "NO_INSURANCE":
      return "No Insurance";
    default:
      return status;
  }
}

function statusBadgeClass(status) {
  if (!status) return "pa__badge";
  switch (status.toUpperCase()) {
    case "SUBMITTED":
      return "pa__badge pa__badge--submitted";
    case "LEAD_CREATED":
      return "pa__badge pa__badge--lead-created";
    case "NO_INSURANCE":
      return "pa__badge pa__badge--no-insurance";
    default:
      return "pa__badge";
  }
}

function sourceLabel(src) {
  if (!src) return "\u2014";
  switch (src.toLowerCase()) {
    case "prep":
      return "PrEP";
    case "sfaf":
      return "SFAF";
    default:
      return src;
  }
}

// ─── Summary Cards ───────────────────────────────────────────────────────────

const SummaryCards = ({ data, loading }) => {
  const cards = [
    { label: "Total Leads", value: data?.totalLeads ?? "\u2014" },
    { label: "Submitted", value: data?.submitted ?? "\u2014" },
    { label: "Drop-Off", value: data?.dropOff ?? "\u2014" },
    { label: "No Insurance", value: data?.noInsurance ?? "\u2014" },
    {
      label: "Conversion Rate",
      value: data?.conversionRate != null ? `${data.conversionRate}%` : "\u2014",
      accent: true,
    },
  ];

  return (
    <div className="pa__summary">
      {cards.map((c) => (
        <div key={c.label} className={`pa__card${c.accent ? " pa__card--accent" : ""}`}>
          <div className="pa__card-label">{c.label}</div>
          <div className="pa__card-value">
            {loading ? <Spinner animation="border" size="sm" /> : c.value}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Lead Detail Panel ───────────────────────────────────────────────────────

const LeadDetail = ({ leadId, onClose, onResendSuccess, timezone }) => {
  const { showSnackbar } = useContext(SnackbarContext);
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resending, setResending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ApiService.getPrepAnalyticsLeadById(leadId);
      setLead(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setError("Lead not found.");
      } else {
        setError("Failed to load lead details.");
      }
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleResend = async () => {
    setShowConfirm(false);
    setResending(true);
    try {
      await ApiService.resendPrepLeadLink(leadId);
      showSnackbar({ message: "JotForm link resent successfully.", severity: "success" });
      setLead((prev) => prev ? { ...prev, reminderCount: (prev.reminderCount || 0) + 1 } : prev);
      if (onResendSuccess) onResendSuccess(leadId);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        showSnackbar({ message: "Lead not found.", severity: "error" });
      } else {
        showSnackbar({ message: "Failed to resend link. Please try again.", severity: "error" });
      }
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <div className="pa__detail">
        <div className="pa__loading">
          <Spinner animation="border" />
          <div style={{ marginTop: 12 }}>Loading lead details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pa__detail">
        <div className="pa__error">
          {error}
          <br />
          <button className="pa__action-btn" onClick={onClose}>Back to list</button>
        </div>
      </div>
    );
  }

  if (!lead) return null;

  const fields = [
    { label: "Name", value: `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || "\u2014" },
    { label: "Email", value: lead.email || "\u2014" },
    { label: "Phone", value: lead.phoneNumber || "\u2014" },
    { label: "Status", value: statusLabel(lead.status) },
    { label: "Source", value: sourceLabel(lead.ingressType) },
    { label: "Insurance", value: lead.privateHealthInsurance || "\u2014" },
    { label: "Consent", value: lead.consent ? "Yes" : "No" },
    { label: "Created", value: formatDateTime(lead.createdAt, timezone) },
    { label: "Submitted", value: formatDateTime(lead.submittedAt, timezone) },
    { label: "Updated", value: formatDateTime(lead.updatedAt, timezone) },
    { label: "Reminders Sent", value: lead.reminderCount ?? 0 },
    { label: "JotForm Submission ID", value: lead.jotformSubmissionId || "\u2014" },
    { label: "Existing User ID", value: lead.existingUserId || "\u2014" },
    { label: "Original Form URL", value: lead.originalFormURL || "\u2014" },
    { label: "UTM Source", value: lead.utmSource || "\u2014" },
    { label: "UTM Medium", value: lead.utmMedium || "\u2014" },
    { label: "UTM Campaign", value: lead.utmCampaign || "\u2014" },
    { label: "UTM Term", value: lead.utmTerm || "\u2014" },
    { label: "GCLID", value: lead.gclid || "\u2014" },
    { label: "Landing Page", value: lead.landingPage || "\u2014" },
  ];

  return (
    <>
      <div className="pa__detail">
        <div className="pa__detail-header">
          <h3 className="pa__detail-title">Lead Detail</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="pa__action-btn--primary pa__action-btn"
              onClick={() => setShowConfirm(true)}
              disabled={resending}
            >
              {resending ? <Spinner animation="border" size="sm" /> : "Resend JotForm Link"}
            </button>
            <button className="pa__action-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        <div className="pa__detail-grid">
          {fields.map((f) => (
            <div key={f.label} className="pa__detail-field">
              <span className="pa__detail-label">{f.label}</span>
              <span className="pa__detail-value">{f.value}</span>
            </div>
          ))}
        </div>
      </div>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Confirm Resend</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Resend the JotForm intake link via email and SMS to{" "}
          <strong>{lead.firstName} {lead.lastName}</strong> ({lead.email})?
        </Modal.Body>
        <div className="pa__modal-footer">
          <button className="pa__modal-btn-cancel" onClick={() => setShowConfirm(false)}>
            Cancel
          </button>
          <button className="pa__modal-btn-export" onClick={handleResend}>
            Confirm & Send
          </button>
        </div>
      </Modal>
    </>
  );
};

// ─── Main PrepAnalytics Component ────────────────────────────────────────────

const PrepAnalytics = ({ resetView }) => {
  const { showSnackbar } = useContext(SnackbarContext);

  // Summary
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Leads list
  const [leads, setLeads] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadsError, setLeadsError] = useState(null);

  // Pagination cursors: stack of lastEvaluatedKey values for back-navigation
  const [cursorStack, setCursorStack] = useState([]);
  const [currentCursor, setCurrentCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState(getInitialTimezone);

  // Detail
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  // Export
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const ALL_EXPORT_FIELDS = [
    { key: "firstName", label: "First Name" },
    { key: "lastName", label: "Last Name" },
    { key: "email", label: "Email" },
    { key: "phoneNumber", label: "Phone" },
    { key: "status", label: "Status" },
    { key: "ingressType", label: "Source" },
    { key: "privateHealthInsurance", label: "Insurance" },
    { key: "createdAt", label: "Created" },
    { key: "submittedAt", label: "Submitted" },
    { key: "utmSource", label: "UTM Source" },
    { key: "utmMedium", label: "UTM Medium" },
    { key: "utmCampaign", label: "UTM Campaign" },
    { key: "utmTerm", label: "UTM Term" },
    { key: "gclid", label: "GCLID" },
    { key: "landingPage", label: "Landing Page" },
  ];
  const [exportFields, setExportFields] = useState(() => ALL_EXPORT_FIELDS.map((f) => f.key));
  const [exportStatus, setExportStatus] = useState("");
  const [exportSource, setExportSource] = useState("");
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [exportSearch, setExportSearch] = useState("");
  const [exportTimezone, setExportTimezone] = useState(getInitialTimezone);

  const openExportModal = () => {
    setExportStatus(statusFilter);
    setExportSource(sourceFilter);
    setExportFrom(fromDate);
    setExportTo(toDate);
    setExportSearch(searchTerm);
    setExportTimezone(selectedTimezone);
    setShowExportModal(true);
  };

  const toggleExportField = (key) => {
    setExportFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (exportStatus) params.status = exportStatus;
      if (exportSource) params.ingressType = exportSource;
      if (exportSearch) params.search = exportSearch;
      if (exportFrom) params.from = new Date(exportFrom + "T00:00:00Z").toISOString();
      if (exportTo) params.to = new Date(exportTo + "T23:59:59.999Z").toISOString();
      if (exportFields.length > 0 && exportFields.length < ALL_EXPORT_FIELDS.length) {
        params.fields = exportFields;
      }
      if (exportTimezone) params.timezone = exportTimezone;
      const res = await ApiService.exportPrepAnalyticsLeads(params);
      const blob = res.data;
      const disposition = res.headers?.["content-disposition"] || "";
      const filename = disposition.match(/filename="(.+)"/)?.[1] ?? "prep-leads-export.csv";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setShowExportModal(false);
      showSnackbar({ message: "Export downloaded successfully.", severity: "success" });
    } catch (err) {
      console.error("Export failed", err);
      showSnackbar({ message: "Failed to export leads.", severity: "error" });
    } finally {
      setExporting(false);
    }
  };

  // Debounce search
  const debounceRef = useRef(null);
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchTerm(val);
    }, 300);
  };

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await ApiService.getPrepAnalyticsSummary();
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to load summary", err);
      showSnackbar({ message: "Failed to load summary data.", severity: "error" });
    } finally {
      setSummaryLoading(false);
    }
  }, [showSnackbar]);

  // Fetch leads
  const fetchLeads = useCallback(async (cursor) => {
    setLeadsLoading(true);
    setLeadsError(null);
    try {
      const params = { limit: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.ingressType = sourceFilter;
      if (searchTerm) params.search = searchTerm;
      if (fromDate) params.from = new Date(fromDate + "T00:00:00Z").toISOString();
      if (toDate) params.to = new Date(toDate + "T23:59:59.999Z").toISOString();
      if (cursor) params.lastEvaluatedKey = cursor;

      const res = await ApiService.getPrepAnalyticsLeads(params);
      const data = res.data || {};
      setLeads(data.items || []);
      setNextCursor(data.lastEvaluatedKey || null);
      if (data.totalCount != null && data.totalCount >= 0) {
        setTotalCount(data.totalCount);
      }
    } catch (err) {
      console.error("Failed to load leads", err);
      setLeadsError("Failed to load leads. Please try again.");
    } finally {
      setLeadsLoading(false);
    }
  }, [statusFilter, sourceFilter, searchTerm, fromDate, toDate]);

  // Reset to first page when filters change
  useEffect(() => {
    setCursorStack([]);
    setCurrentCursor(null);
    setNextCursor(null);
    setPageIndex(0);
    setSelectedLeadId(null);
    fetchLeads(null);
  }, [statusFilter, sourceFilter, searchTerm, fromDate, toDate, fetchLeads]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleNextPage = () => {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, currentCursor]);
    setCurrentCursor(nextCursor);
    setPageIndex((prev) => prev + 1);
    fetchLeads(nextCursor);
  };

  const handlePrevPage = () => {
    if (cursorStack.length === 0) return;
    const newStack = [...cursorStack];
    const prevCursor = newStack.pop();
    setCursorStack(newStack);
    setCurrentCursor(prevCursor);
    setPageIndex((prev) => prev - 1);
    fetchLeads(prevCursor);
  };

  const handleResendFromTable = async (leadId) => {
    if (!window.confirm("Resend JotForm link to this lead?")) return;
    try {
      await ApiService.resendPrepLeadLink(leadId);
      showSnackbar({ message: "Link resent successfully.", severity: "success" });
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId ? { ...l, reminderCount: (l.reminderCount || 0) + 1 } : l
        )
      );
    } catch (err) {
      showSnackbar({ message: "Failed to resend link.", severity: "error" });
    }
  };

  const handleResendSuccessFromDetail = (leadId) => {
    setLeads((prev) =>
      prev.map((l) =>
        l.id === leadId ? { ...l, reminderCount: (l.reminderCount || 0) + 1 } : l
      )
    );
  };

  const rangeStart = pageIndex * PAGE_SIZE + 1;
  const rangeEnd = pageIndex * PAGE_SIZE + leads.length;

  return (
    <div className="pa">
      <div className="pa__top">
        <button className="pa__back" onClick={resetView}>
          <BsArrowLeft /> Back to Admin Panel
        </button>
      </div>

      <h2 className="pa__title">Prep Lead Analytics</h2>

      <SummaryCards data={summary} loading={summaryLoading} />

      {/* Detail view */}
      {selectedLeadId && (
        <LeadDetail
          leadId={selectedLeadId}
          onClose={() => setSelectedLeadId(null)}
          onResendSuccess={handleResendSuccessFromDetail}
          timezone={selectedTimezone}
        />
      )}

      {/* Filters */}
      <div className="pa__filters">
        <div className="pa__filter-group">
          <span className="pa__filter-label">Status</span>
          <div style={{ display: "flex", gap: 4 }}>
            {[
              { value: "", label: "All" },
              { value: "LEAD_CREATED", label: "Lead Created" },
              { value: "SUBMITTED", label: "Submitted" },
              { value: "NO_INSURANCE", label: "No Insurance" },
            ].map((opt) => (
              <button
                key={opt.value}
                className={`pa__filter-btn${statusFilter === opt.value ? " pa__filter-btn--active" : ""}`}
                onClick={() => setStatusFilter(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pa__filter-group">
          <span className="pa__filter-label">Source</span>
          <select
            className="pa__filter-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="">All Sources</option>
            <option value="prep">PrEP</option>
            <option value="sfaf">SFAF</option>
          </select>
        </div>

        <div className="pa__filter-group">
          <span className="pa__filter-label">From</span>
          <input
            type="date"
            className="pa__filter-input pa__filter-input--date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>

        <div className="pa__filter-group">
          <span className="pa__filter-label">To</span>
          <input
            type="date"
            className="pa__filter-input pa__filter-input--date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>

        <div className="pa__filter-group">
          <span className="pa__filter-label">Timezone</span>
          <select
            className="pa__filter-select"
            value={selectedTimezone}
            onChange={(e) => setSelectedTimezone(e.target.value)}
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>

        <div className="pa__filter-group" style={{ flex: 1, minWidth: 180 }}>
          <input
            type="text"
            className="pa__filter-input pa__filter-input--search"
            placeholder="Search name or email..."
            value={searchInput}
            onChange={handleSearchChange}
            style={{ width: "100%" }}
          />
        </div>

        <button
          className="pa__export-btn"
          onClick={openExportModal}
        >
          Export
        </button>
      </div>

      {/* Table */}
      <div className="pa__table-wrap">
        {leadsLoading ? (
          <div className="pa__loading">
            <Spinner animation="border" />
            <div style={{ marginTop: 12 }}>Loading leads...</div>
          </div>
        ) : leadsError ? (
          <div className="pa__error">
            {leadsError}
            <br />
            <button className="pa__action-btn" onClick={() => fetchLeads(currentCursor)}>
              Retry
            </button>
          </div>
        ) : leads.length === 0 ? (
          <div className="pa__empty">No leads found.</div>
        ) : (
          <>
            <table className="pa__table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Created</th>
                  <th>Submitted</th>
                  <th>Reminders</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id}>
                    <td>{`${lead.firstName || ""} ${lead.lastName || ""}`.trim() || "\u2014"}</td>
                    <td>{lead.email || "\u2014"}</td>
                    <td>
                      <span className={statusBadgeClass(lead.status)}>
                        {statusLabel(lead.status)}
                      </span>
                    </td>
                    <td>{sourceLabel(lead.ingressType)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{formatDate(lead.createdAt, selectedTimezone)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{formatDate(lead.submittedAt, selectedTimezone)}</td>
                    <td style={{ textAlign: "center" }}>{lead.reminderCount ?? 0}</td>
                    <td>
                      <div className="pa__actions-cell">
                        <button
                          className="pa__action-btn"
                          onClick={() => setSelectedLeadId(lead.id)}
                        >
                          View
                        </button>
                        <button
                          className="pa__action-btn"
                          onClick={() => handleResendFromTable(lead.id)}
                        >
                          Resend
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pa__pagination">
              <span className="pa__pagination-info">
                {totalCount > 0
                  ? `Showing ${rangeStart}\u2013${rangeEnd} of ${totalCount}`
                  : `Page ${pageIndex + 1}`}
              </span>
              <div className="pa__pagination-buttons">
                <button
                  className="pa__action-btn"
                  onClick={handlePrevPage}
                  disabled={pageIndex === 0}
                >
                  Previous
                </button>
                <button
                  className="pa__action-btn"
                  onClick={handleNextPage}
                  disabled={!nextCursor}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Export Modal */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered size="lg">
        <div className="pa__modal-header">
          <h3 className="pa__modal-title">Export Leads as CSV</h3>
          <p className="pa__modal-subtitle">
            Choose filters and columns, then export matching leads.
          </p>
        </div>
        <Modal.Body style={{ padding: 0 }}>
          <div className="pa__modal-section">
            <div className="pa__modal-section-label">Filters</div>
            <div className="pa__modal-filters">
              <div className="pa__modal-filter-row">
                <label className="pa__modal-filter-item">
                  <span className="pa__modal-filter-name">Status</span>
                  <select
                    className="pa__modal-select"
                    value={exportStatus}
                    onChange={(e) => setExportStatus(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="LEAD_CREATED">Lead Created</option>
                    <option value="SUBMITTED">Submitted</option>
                    <option value="NO_INSURANCE">No Insurance</option>
                  </select>
                </label>
                <label className="pa__modal-filter-item">
                  <span className="pa__modal-filter-name">Source</span>
                  <select
                    className="pa__modal-select"
                    value={exportSource}
                    onChange={(e) => setExportSource(e.target.value)}
                  >
                    <option value="">All Sources</option>
                    <option value="prep">PrEP</option>
                    <option value="sfaf">SFAF</option>
                  </select>
                </label>
              </div>
              <div className="pa__modal-filter-row">
                <label className="pa__modal-filter-item">
                  <span className="pa__modal-filter-name">From</span>
                  <input
                    type="date"
                    className="pa__modal-input"
                    value={exportFrom}
                    onChange={(e) => setExportFrom(e.target.value)}
                  />
                </label>
                <label className="pa__modal-filter-item">
                  <span className="pa__modal-filter-name">To</span>
                  <input
                    type="date"
                    className="pa__modal-input"
                    value={exportTo}
                    onChange={(e) => setExportTo(e.target.value)}
                  />
                </label>
              </div>
              <label className="pa__modal-filter-item" style={{ width: "100%" }}>
                <span className="pa__modal-filter-name">Search</span>
                <input
                  type="text"
                  className="pa__modal-input"
                  placeholder="Search name or email..."
                  value={exportSearch}
                  onChange={(e) => setExportSearch(e.target.value)}
                />
              </label>
              <label className="pa__modal-filter-item">
                <span className="pa__modal-filter-name">Timezone</span>
                <select
                  className="pa__modal-select"
                  value={exportTimezone}
                  onChange={(e) => setExportTimezone(e.target.value)}
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className="pa__modal-section">
            <div className="pa__modal-section-label">Columns to include</div>
            <div className="pa__export-fields">
              {ALL_EXPORT_FIELDS.map((f) => (
                <label key={f.key} className="pa__export-field-label">
                  <input
                    type="checkbox"
                    checked={exportFields.includes(f.key)}
                    onChange={() => toggleExportField(f.key)}
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </div>
        </Modal.Body>
        <div className="pa__modal-footer">
          <button className="pa__modal-btn-cancel" onClick={() => setShowExportModal(false)}>
            Cancel
          </button>
          <button
            className="pa__modal-btn-export"
            onClick={handleExport}
            disabled={exporting || exportFields.length === 0}
          >
            {exporting ? <Spinner animation="border" size="sm" /> : "Export CSV"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PrepAnalytics;
